import axios, { AxiosResponse } from 'axios';

const { CancelToken } = axios;

export enum AccessPolicy {
    Public = 0,
    Private = 1,
}

export interface S3MetaArgs {
    accessPolicy: AccessPolicy;
    expireDate: number | null;
    fileName: string;
    moduleId: string;
}

export interface S3MetaOptions {
    contentType: string;
    version: string;
    args: S3MetaArgs;
}

export interface Asset {
    id?: string;
    /** path to the file */
    objectID: string;
    /** asset key */
    assetKey: string;
    moduleID?: string;
    /** S3 url */
    internalURL: string;
    /** RDC url */
    externalURL?: string;
    /** file name uploaded to S3 */
    fileName: string;
    /** extension of the file name uploaded to S3 */
    extension: string;
    /** whether public or private */
    accessPolicy: AccessPolicy;
    contentLength?: number;
    expireDate?: number;
    etag?: string; // READONLY
    originalUri?: string;
    mediaMetadata?: object;
}

const assetsBaseUrl = (tenantId: number): string => {
    return `/api/tenants/${tenantId}/assets`;
};

let cancelGetS3MetaSource = CancelToken.source();
let cancelSaveAssetSource = CancelToken.source();

export const getS3Meta = async ({
    agencyId,
    tenantId,
    options,
    handleUploadProgress,
}: {
    agencyId: number;
    tenantId: number;
    options: S3MetaOptions;
    handleUploadProgress?: any;
}): Promise<any> => {
    const params: any = { agencyId, tenantId, ...options };

    const response: AxiosResponse = await axios.post(`${assetsBaseUrl(tenantId)}/s3Meta`, params, {
        cancelToken: cancelGetS3MetaSource.token,
        onUploadProgress: (event: any) => {
            if (handleUploadProgress) {
                const progress = event.total ? (event.loaded / event.total) * 100 : 100;

                handleUploadProgress(progress);
            }
        },
    });

    return response.data;
};

export const cancelGetS3Meta = (): void => {
    cancelGetS3MetaSource.cancel();
    cancelGetS3MetaSource = CancelToken.source();
};

export const saveAsset = async ({
    agencyId,
    tenantId,
    asset,
    args,
    handleUploadProgress,
}: {
    agencyId: number;
    tenantId: number;
    asset: Asset;
    args?: { shortenExternalUrl?: boolean };
    handleUploadProgress?: any;
}): Promise<any> => {
    const params: any = { agencyId, tenantId, asset, args };

    const response: AxiosResponse = await axios.post(`${assetsBaseUrl(tenantId)}`, params, {
        cancelToken: cancelSaveAssetSource.token,
        onUploadProgress: (event: any) => {
            if (handleUploadProgress) {
                const progress = event.total ? (event.loaded / event.total) * 100 : 100;

                handleUploadProgress(progress);
            }
        },
    });

    return response.data;
};

export const cancelSaveAsset = (): void => {
    cancelSaveAssetSource.cancel();
    cancelSaveAssetSource = CancelToken.source();
};

export const getAssets = async (agencyId: number, tenantId: number, search: any, args: any = {}): Promise<any> => {
    const { data } = await axios.get(`${assetsBaseUrl(tenantId)}`, {
        params: { agencyId, tenantId, search, args },
    });

    return data;
};
